import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  arrayUnion, 
  arrayRemove 
} from 'firebase/firestore';
import { db } from '../firebase/config';

export const familyService = {
  // Create a new family
  async createFamily(userId) {
    try {
      const familyRef = doc(collection(db, 'families'));
      const familyId = familyRef.id;

      const familyData = {
        id: familyId,
        members: [userId],
        createdBy: userId,
        createdAt: new Date(),
        updatedAt: new Date(),
        settings: {
          currency: 'BDT',
          budgetStartDay: 1,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }
      };

      await setDoc(familyRef, familyData);
      
      return { success: true, familyId, family: familyData };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get family by ID
  async getFamilyById(familyId) {
    try {
      const familyDoc = await getDoc(doc(db, 'families', familyId));
      
      if (familyDoc.exists()) {
        return { success: true, family: familyDoc.data() };
      } else {
        return { success: false, error: 'Family not found' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Add member to family
  async addMemberToFamily(familyId, userId) {
    try {
      const familyRef = doc(db, 'families', familyId);
      
      // Check if family exists
      const familyDoc = await getDoc(familyRef);
      if (!familyDoc.exists()) {
        return { success: false, error: 'Family not found' };
      }

      const familyData = familyDoc.data();
      
      // Check if user is already a member
      if (familyData.members.includes(userId)) {
        return { success: false, error: 'User is already a member of this family' };
      }

      // Add user to family members
      await updateDoc(familyRef, {
        members: arrayUnion(userId),
        updatedAt: new Date()
      });

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Remove member from family
  async removeMemberFromFamily(familyId, userId) {
    try {
      const familyRef = doc(db, 'families', familyId);
      
      // Check if family exists
      const familyDoc = await getDoc(familyRef);
      if (!familyDoc.exists()) {
        return { success: false, error: 'Family not found' };
      }

      const familyData = familyDoc.data();
      
      // Check if user is a member
      if (!familyData.members.includes(userId)) {
        return { success: false, error: 'User is not a member of this family' };
      }

      // Don't allow removing the creator if they're the only member
      if (familyData.members.length === 1 && familyData.createdBy === userId) {
        return { success: false, error: 'Cannot remove the last member who is also the creator' };
      }

      // Remove user from family members
      await updateDoc(familyRef, {
        members: arrayRemove(userId),
        updatedAt: new Date()
      });

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Update family settings
  async updateFamilySettings(familyId, settings) {
    try {
      const familyRef = doc(db, 'families', familyId);
      
      await updateDoc(familyRef, {
        settings: {
          ...settings
        },
        updatedAt: new Date()
      });

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get family members details
  async getFamilyMembers(familyId) {
    try {
      const familyDoc = await getDoc(doc(db, 'families', familyId));
      
      if (!familyDoc.exists()) {
        return { success: false, error: 'Family not found' };
      }

      const familyData = familyDoc.data();
      const memberIds = familyData.members;

      // Get details for each member
      const memberPromises = memberIds.map(async (memberId) => {
        const memberDoc = await getDoc(doc(db, 'users', memberId));
        return memberDoc.exists() ? { id: memberId, ...memberDoc.data() } : null;
      });

      const members = await Promise.all(memberPromises);
      const validMembers = members.filter(member => member !== null);

      return { success: true, members: validMembers };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Check if user is family member
  async isUserFamilyMember(familyId, userId) {
    try {
      const familyDoc = await getDoc(doc(db, 'families', familyId));
      
      if (!familyDoc.exists()) {
        return { success: false, error: 'Family not found' };
      }

      const familyData = familyDoc.data();
      const isMember = familyData.members.includes(userId);

      return { success: true, isMember };
    } catch (error) {
      return { success: false, error: error.message };
    }
  },

  // Get family statistics
  async getFamilyStats(familyId) {
    try {
      const familyDoc = await getDoc(doc(db, 'families', familyId));
      
      if (!familyDoc.exists()) {
        return { success: false, error: 'Family not found' };
      }

      const familyData = familyDoc.data();
      
      // Basic stats
      const stats = {
        memberCount: familyData.members.length,
        createdAt: familyData.createdAt,
        settings: familyData.settings
      };

      return { success: true, stats };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
};
